/**
 * The coverage strip — `APPLICATION_SPEC.md` §5.3.
 *
 * **A coverage instrument, not a status widget.** A generic uptime strip has
 * three states and no notion of *lost*; this one has five, and the fifth
 * (`critical`) means data that can never be recovered because the day has left
 * Snap's 90-day window. That is the whole reason it exists rather than a row of
 * green ticks.
 *
 * Three rules from the spec, each of which is a way a thin bar goes wrong:
 *
 * 1. **The hit target is padded well beyond the visual cell.** A ~5px bar is a
 *    pinpoint target, which is an explicit anti-pattern — the interactive area
 *    meets ~24px and swallows the gap between cells.
 * 2. **Never colour alone.** Three affordances: the tooltip names the status in
 *    words, a legend uses the existing `StatusDot`, and the run-history table
 *    below is the required table-view twin.
 * 3. **Cells are separated by a surface gap, never a border** (mark spec), and
 *    the strip scrolls in **its own** container so the page body never scrolls
 *    sideways.
 *
 * The state → token mapping is **not here.** The server sends `token` with each
 * day, so there is exactly one place for `critical` to stop meaning
 * `--status-critical`.
 */
import { useState } from 'react'

import { HintBubble } from './Hint'
import { StatusDot, type StatusState } from './StatusDot'
import type { Strip, StripDay } from '../lib/api'
import { formatAxisDate, formatDate } from '../lib/dates'
import { formatNumber } from '../lib/numbers'

/** Widest a cell grows to; it shrinks to fit a long window before scrolling. */
const CELL = '0.5rem'
const GAP = '2px'

/** The strip is one cell per day, so its tick labels use the compact axis form.
 *  The hover card and the aria-label carry the full `MM/DD/YYYY`. */
const formatDay = formatAxisDate


/**
 * **A colour key, not a restatement of the cells.**
 *
 * 🔴 It used to be both, and that broke the moment day labels went per-pipeline
 * (`coverage.py`): this legend read *"Failed, Still Recoverable"* directly above
 * a ClickUp cell whose own tooltip said *"Nothing updated. Nothing is lost —
 * later runs re-read everything."* Two different sentences for one colour, on
 * one screen.
 *
 * One legend sits above all three cards, so it can only name what a colour
 * *means* — the state. What a failure **costs** differs by pipeline and belongs
 * on the cell, where the reader is already asking about one specific day. Keep
 * these to the bare state names, and never copy a sentence from the server's
 * label table into here.
 *
 * `critical` stays even though only Snap can reach it: the key covers every card
 * on the page, and Snap is one of them.
 */
const LEGEND: { state: StripDay['state']; token: StatusState; label: string }[] = [
  { state: 'complete', token: 'good', label: 'Complete' },
  { state: 'partial', token: 'warning', label: 'Partial' },
  { state: 'failed', token: 'serious', label: 'Failed' },
  { state: 'critical', token: 'critical', label: 'No Longer Recoverable' },
  { state: 'not_attempted', token: 'idle', label: 'Not Attempted' },
]

/**
 * The hover content. **The frame and the position belong to `HintBubble`.**
 *
 * 🔴 The first version positioned itself: `position: absolute` inside the cell.
 * It was invisible, and the reason is a lesson this repo had already written
 * down once — §5.3 requires the strip to scroll in **its own container**, and
 * `overflow-x: auto` makes `overflow-y` compute to `auto` too, so an absolutely
 * positioned bubble above a cell is clipped by the scroller it lives in.
 * Measured: the bubble's top was 201px and the clipping ancestor's was 307px,
 * so all 103px of it sat outside and rendered nowhere.
 *
 * `HintBubble` is `position: fixed` off the target's own rect for exactly this
 * reason — its docstring says so — and it clamps to the viewport and flips
 * below when there is no room above.
 */
function TooltipBody({ day, basis }: { day: StripDay; basis: Strip['basis'] }) {
  return (
    <div style={{ textAlign: 'left', minWidth: '11rem' }}>
      <div className="font-semibold">{formatDate(day.day)}</div>
      {/* The status in words — affordance one. */}
      <div className="mt-1">{day.label}</div>
      {basis === 'coverage_ledger' ? (
        <dl className="mt-2 mb-0 grid gap-x-3" style={{ gridTemplateColumns: 'auto auto' }}>
          <dt style={{ color: 'var(--text-label)' }}>Units landed</dt>
          <dd className="m-0 is-num">
            {day.covered} of {Math.max(day.expected - day.unavailable, 0)}
          </dd>
          <dt style={{ color: 'var(--text-label)' }}>Channels</dt>
          <dd className="m-0 is-num">{day.channels}</dd>
          {day.failed > 0 && (
            <>
              <dt style={{ color: 'var(--text-label)' }}>Failed</dt>
              <dd className="m-0 is-num">{day.failed}</dd>
            </>
          )}
          {day.claimed > 0 && (
            <>
              <dt style={{ color: 'var(--text-label)' }}>Stuck claims</dt>
              <dd className="m-0 is-num">{day.claimed}</dd>
            </>
          )}
          <dt style={{ color: 'var(--text-label)' }}>Rows</dt>
          <dd className="m-0 is-num">{formatNumber(day.rows)}</dd>
          {day.chunk_duration_seconds !== null && (
            <>
              {/* Labelled "chunk", not "duration". A work unit spans a multi-day
                  chunk, so a per-day figure would be invented precision. */}
              <dt style={{ color: 'var(--text-label)' }}>Chunk fetch</dt>
              <dd className="m-0 is-num">
                {day.chunk_duration_seconds < 90
                  ? `${day.chunk_duration_seconds.toFixed(1)}s`
                  : `${Math.round(day.chunk_duration_seconds / 60)}m`}
              </dd>
            </>
          )}
        </dl>
      ) : (
        <div className="mt-2" style={{ color: 'var(--text-label)' }}>
          {day.covered} run{day.covered === 1 ? '' : 's'} succeeded
          {day.failed ? `, ${day.failed} failed` : ''}
        </div>
      )}
      {day.last_error && (
        // §5.3's hover contract, and the reason it names the attempt log: the
        // units table overwrites its error on retry, so a successful retry
        // would erase the evidence that page size — not rate — was the problem.
        <div
          className="mt-2 is-mono"
          style={{ color: 'var(--status-critical)', wordBreak: 'break-word' }}
        >
          {day.last_error_http_status ? `${day.last_error_http_status} · ` : ''}
          {day.last_error.length > 160 ? `${day.last_error.slice(0, 160)}…` : day.last_error}
        </div>
      )}
      {day.state === 'partial' && basis === 'coverage_ledger' && (
        <div className="mt-2" style={{ color: 'var(--text-label)' }}>
          Select to See Which Channels Are Missing.
        </div>
      )}
    </div>
  )
}

export function CoverageStrip({
  strip,
  boundary,
  historyStart,
  onSelectDay,
  selectedDay,
  showName = true,
}: {
  strip: Strip
  /** The earliest still-reachable day. Marked, because it advances daily and
   *  anything left of it that is not complete is permanently lost. */
  boundary: string
  /** The client's requested history start — marked at the left edge. */
  historyStart: string
  onSelectDay?: (day: string) => void
  selectedDay?: string | null
  /** False inside a source card, where the card's own heading names it — a
   *  strip labelled `snap` under a heading that says Snapchat is the same
   *  word twice, in two vocabularies. */
  showName?: boolean
}) {
  const [hovered, setHovered] = useState<{ day: string; rect: DOMRect } | null>(null)

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        {showName && (
          <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
            {strip.pipeline}
          </span>
        )}
        {/* The contracted range, not the whole strip. Counting the days before
            20 Jul — which nobody asked for — reported 49 of 91 on a range that
            was whole.
            
            And the *verb* differs by basis: a ledger-backed strip knows a day
            is complete per channel and dataset, while a run-history strip only
            knows a run succeeded that day. "Complete" would claim the first
            while measuring the second. */}
        <span className="text-xs is-num" style={{ color: 'var(--text-label)' }}>
          {strip.complete_in_history} of {strip.history_days}{' '}
          {strip.basis === 'coverage_ledger' ? 'days complete' : 'days ran'}
        </span>
        {/* A run-history strip cannot see a per-channel gap. Saying so on the
            strip itself, not only in a footnote, is the difference between a
            caveat and a disclaimer nobody reads. */}
        {strip.basis === 'run_history' && (
          <span className="text-xs" style={{ color: 'var(--text-label)' }}>
            · From Run History, Not per Channel
          </span>
        )}
      </div>

      <div style={{ overflowX: 'auto', paddingTop: '0.25rem' }}>
        <div
          role="list"
          aria-label={`${strip.pipeline} Coverage, One Cell per Day`}
          className="flex"
          style={{ gap: GAP, minWidth: 'min-content' }}
        >
          {strip.days.map((day) => {
            const isBoundary = day.day === boundary
            const isHistoryStart = day.day === historyStart
            const selected = selectedDay === day.day
            return (
              <div
                key={day.day}
                role="listitem"
                style={{ position: 'relative', flex: `1 1 ${CELL}`, minWidth: '3px' }}
              >
                <button
                  type="button"
                  // The hit target, not the mark: ~24px tall including the
                  // vertical padding, and it swallows the gap either side.
                  // A 5px bar is a pinpoint target.
                  // The rect is what the fixed-position bubble is placed
                  // against, so it has to come from the event's own target.
                  onMouseEnter={(event) =>
                    setHovered({
                      day: day.day,
                      rect: event.currentTarget.getBoundingClientRect(),
                    })
                  }
                  onMouseLeave={() => setHovered(null)}
                  onFocus={(event) =>
                    setHovered({
                      day: day.day,
                      rect: event.currentTarget.getBoundingClientRect(),
                    })
                  }
                  onBlur={() => setHovered(null)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setHovered(null)
                  }}
                  onClick={() => onSelectDay?.(day.day)}
                  aria-label={`${formatDate(day.day)}: ${day.label}`}
                  aria-pressed={selected}
                  style={{
                    display: 'block',
                    width: '100%',
                    minHeight: '24px',
                    padding: `0.35rem calc(${GAP} / 2)`,
                    background: 'transparent',
                    border: 'none',
                    cursor: onSelectDay ? 'pointer' : 'default',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'block',
                      height: selected ? '1.6rem' : '1.4rem',
                      background: `var(--status-${day.token})`,
                      borderRadius: '2px',
                      // Shadow means "this floats" — a selected cell is the one
                      // thing here that is lifted, and it is the only outline
                      // that is not a border between marks.
                      outline: selected ? '2px solid var(--brand-ink)' : 'none',
                      outlineOffset: '1px',
                    }}
                  />
                </button>

                {isBoundary && (
                  <span
                    aria-hidden="true"
                    title="Earliest Day Snap Will Still Serve"
                    style={{
                      position: 'absolute',
                      left: `-1px`,
                      top: 0,
                      bottom: '-0.35rem',
                      borderLeft: '2px dashed var(--text-label)',
                    }}
                  />
                )}
                {isHistoryStart && (
                  <span
                    aria-hidden="true"
                    title="Requested History Start: 20 Jul 2026"
                    style={{
                      position: 'absolute',
                      left: '-1px',
                      top: 0,
                      bottom: '-0.35rem',
                      borderLeft: '2px solid var(--brand-ink)',
                    }}
                  />
                )}

              </div>
            )
          })}
        </div>

        {/* A sparse date axis. Without it the strip proves capture but never says
            *when*, and "did we get 20 July?" is the question it exists for. */}
        <div className="mt-1 flex" style={{ gap: GAP, minWidth: 'min-content' }}>
          {strip.days.map((day, index) => (
            <div
              key={day.day}
              style={{ flex: `1 1 ${CELL}`, minWidth: '3px', position: 'relative', height: '0.9rem' }}
            >
              {(index === 0
                || index === strip.days.length - 1
                // Dropped near the end, or it collides with the
                // right-anchored final label.
                || (index % 14 === 0 && index < strip.days.length - 7)) && (
                <span
                  className="text-[0.6rem]"
                  // The last label is anchored to its right edge: anchored left
                  // like the others it overflowed the scroll container and
                  // rendered as "Se".
                  style={{
                    position: 'absolute',
                    ...(index === strip.days.length - 1 ? { right: 0 } : { left: 0 }),
                    whiteSpace: 'nowrap',
                    color: 'var(--axis-label)',
                  }}
                >
                  {formatDay(day.day)}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* One bubble, rendered outside the scrolling container and positioned
          against the hovered cell's rect. Never inside the scroller: that is
          what made the first version invisible. */}
      {hovered && (
        <HintBubble rect={hovered.rect}>
          <TooltipBody
            day={strip.days.find((entry) => entry.day === hovered.day) ?? strip.days[0]!}
            basis={strip.basis}
          />
        </HintBubble>
      )}
    </div>
  )
}

/** Affordance two — the legend, from the existing status components. */
export function CoverageLegend({ states }: { states?: StripDay['state'][] }) {
  const shown = states ? LEGEND.filter((entry) => states.includes(entry.state)) : LEGEND
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
      {shown.map((entry) => (
        <StatusDot key={entry.state} state={entry.token}>
          {entry.label}
        </StatusDot>
      ))}
    </div>
  )
}
